import React, { useState } from 'react';
import useFacebookAccounts from '../../../hooks/useFacebookAccounts';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { PageHeader, RsocNavHeader } from '../';
import MainLayoutDemand from '../../MainLoyout/MainLayoutDemand';
import Api from '../../../Services/Api';

// Import components
import AccountSelector from './components/AccountSelector';
import PageSelector from './components/PageSelector';
import CampaignConfigurator from './components/CampaignConfigurator';
import AdSetConfigurator from './components/AdSetConfigurator';
import AdCreator from './components/AdCreator';
import LaunchSummary from './components/LaunchSummary';
import QuickLauncher from './components/QuickLauncher';

// Other step components will be imported here
// import LaunchSummary from './components/LaunchSummary';

const steps = [
  'Select Account(s)',
  'Select Page',
  'Configure Campaign',
  'Configure Ad Set',
  'Create Ads',
  'Review & Launch'
];

const LauncherPage = () => {
  // Launch mode state
  const [launchMode, setLaunchMode] = useState('manual'); // 'manual' or 'quick'
  
  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  
  // Get Facebook accounts from centralized configuration
  const { accounts: facebookAccounts, loading: accountsLoading, error: accountsError } = useFacebookAccounts('launcher');

  // Available Facebook pages
  const [facebookPages] = useState([
    {
      id: '524097567461962',
      name: 'Expected Unexpected',
      description: 'Facebook page for surprising and unexpected content'
    },
    {
      id: '464195180106831',
      name: 'Explore Now',
      description: 'Facebook page for exploration and discovery content'
    },
    {
      id: '308888415633103',
      name: 'Top Local Service Deals',
      description: 'Facebook page for local service campaigns'
    },
    {
      id: '667805466407711',
      name: 'Anything Related',
      description: 'General purpose Facebook page for diverse content'
    },
    {
      id: '660501717154707',
      name: 'Ask anything',
      description: 'Facebook page for Q&A and general inquiries'
    },
    {
      id: '703053446232156',
      name: 'Mildly Interesting',
      description: 'Facebook page for mildly interesting and engaging content'
    },
    {
      id: '842298358962790',
      name: 'Knowledge Hub',
      description: 'Facebook page for educational and informative content'
    }
  ]);

  // Campaign data state
  const [campaignData, setCampaignData] = useState({
    selectedAccounts: [], // Changed to array for multiple accounts
    selectedPage: null, // RSOC content page
    campaign: {
      name: '',
      objective: 'OUTCOME_SALES',
      status: 'ACTIVE', // Always active, no paused option
      facebookPageId: '524097567461962', // Default Facebook page (Expected Unexpected)
      specialAdCategories: [], // Special ad categories
      startTiming: 'delayed', // Default to delayed start (next day at 00:00)
      start_time: (() => {
        // Calculate next day at midnight in UTC (ad account timezone is UTC)
        const now = new Date();
        const tomorrow = new Date(Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate() + 1, // Next day in UTC
          0, 0, 0, 0 // Midnight UTC
        ));
        return tomorrow.toISOString();
      })(), // Calculate next day at midnight UTC
    },
    adSet: {
      name: '',
      status: 'ACTIVE',
      optimization_goal: 'OFFSITE_CONVERSIONS',
      billing_event: 'IMPRESSIONS',
      budget: 10,
      targeting: {
        countries: [],
        interests: [],
      },
    },
    ads: [],
  });

  // Handle account selection (now supports multiple accounts)
  const handleAccountSelect = (accounts) => {
    setCampaignData(prev => ({
      ...prev,
      selectedAccounts: accounts,
      // Reset page selection when account changes
      selectedPage: null,
      // Reset campaign name when account changes
      campaign: {
        ...prev.campaign,
        name: '',
      },
      adSet: {
        ...prev.adSet,
        name: '',
      }
    }));
  };

  // Handle page selection
  const handlePageSelect = (page) => {
    // Format today's date as YYYY-MM-DD
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    
    // Ensure page has a slug - if none exists, create one from title or use 'default'
    const pageWithSlug = {
      ...page,
      slug: page.slug || (page.title ? page.title.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'default')
    };
    
    console.log('Selected content page with slug:', pageWithSlug);
    
    setCampaignData(prev => ({
      ...prev,
      selectedPage: pageWithSlug,
      // Set campaign name based on slug and today's date
      campaign: {
        ...prev.campaign,
        name: `Widget ${pageWithSlug.slug} - ${formattedDate}`,
        // Set special ad categories from rsoc_spy if available
        specialAdCategories: pageWithSlug.special_ad_categories || prev.campaign.specialAdCategories || []
      },
      // Set ad set name with slug, date and adset suffix
      adSet: {
        ...prev.adSet,
        name: `${pageWithSlug.slug} - ${formattedDate} - adset`,
      }
    }));
  };

  // Handle campaign configuration changes
  const handleCampaignChange = (updatedCampaign) => {
    setCampaignData(prev => ({
      ...prev,
      campaign: updatedCampaign
    }));
  };

  // Handle ad set configuration changes
  const handleAdSetChange = (updatedAdSet) => {
    setCampaignData(prev => ({
      ...prev,
      adSet: updatedAdSet
    }));
  };

  // Handle ads changes
  const handleAdsChange = (updatedAds) => {
    setCampaignData(prev => ({
      ...prev,
      ads: updatedAds
    }));
  };

  // Handle campaign launch
  const handleLaunch = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate campaign data once more before submitting
      if (!isStepValid(activeStep)) {
        setLoading(false);
        return;
      }
      
      // Additional safety check for required data
      if (!campaignData.selectedAccounts || campaignData.selectedAccounts.length === 0) {
        setError('No Facebook accounts selected. Please go back and select at least one account.');
        setLoading(false);
        return;
      }
      
      if (!campaignData.selectedPage) {
        setError('No page selected. Please go back and select a page.');
        setLoading(false);
        return;
      }
      
      // Get properly formatted keywords
      const getFormattedKeywords = () => {
        // Handle different possible keyword formats
        if (!campaignData.selectedPage) return [];
        
        const rawKeywords = campaignData.selectedPage.keywords;
        
        // Handle different formats of keywords
        if (Array.isArray(rawKeywords)) return rawKeywords;
        if (typeof rawKeywords === 'string') {
          // Split by comma and trim each keyword
          return rawKeywords.split(',').map(k => k.trim()).filter(k => k !== '');
        }
        return [];
      };
      
      const keywordsArray = getFormattedKeywords();
      const hasValidKeywords = keywordsArray.length > 0;
      const mainKeyword = hasValidKeywords ? keywordsArray[0].trim() : '';
      const keywordsStr = keywordsArray.join(',');
      const encodedKeywords = encodeURIComponent(keywordsStr || campaignData.selectedPage?.slug || 'default');
      const pageSlug = campaignData.selectedPage?.slug || 'default';
      
      // Create campaigns for each selected account
      const campaignPromises = campaignData.selectedAccounts.map(async (account) => {
        try {
          // Debug: Log the countries being sent
          console.log('🌍 Countries being sent to API:', campaignData.adSet.targeting?.countries);
          console.log('🎯 Full targeting data:', campaignData.adSet.targeting);
          
          // Build campaign object
          const campaignObj = {
            name: campaignData.campaign.name,
            objective: campaignData.campaign.objective,
            status: campaignData.campaign.status,
            account_id: account.id, // Use the current account ID
            page_id: campaignData.campaign.facebookPageId, // Use the selected Facebook page ID
            daily_budget: parseFloat(campaignData.adSet.budget) || 10, // Add daily_budget from adSet budget or default to 10
            special_ad_categories: campaignData.campaign.specialAdCategories || [], // Include special ad categories
          };
          
          // Only include start_time if it's set (for delayed start)
          if (campaignData.campaign.start_time) {
            campaignObj.start_time = campaignData.campaign.start_time;
          }
          
          const response = await Api.createCompleteFacebookCampaign({
            campaign: campaignObj,
            adSet: {
              name: campaignData.adSet.name,
              optimization_goal: 'OFFSITE_CONVERSIONS',
              billing_event: campaignData.adSet.billing_event,
              targeting: campaignData.adSet.targeting,
            },
            ads: campaignData.ads.map(ad => ({
              name: `${campaignData.campaign.name} - ${ad.variationId}`,
              status: 'PAUSED',
              creative: {
                title: ad.copy.headline,
                body: ad.copy.primaryText,
                description: ad.copy.description,
                image_url: ad.image.url,
                link_destination_display_url: hasValidKeywords
                  ? `www.thecozyguide.com/dsr/${mainKeyword}/`
                  : `www.thecozyguide.com/dsr/${pageSlug}/`,
              },
              page_slug: pageSlug,
              destination_url: `https://www.thecozyguide.com/dsr/${pageSlug}/?adx_title=${encodeURIComponent(ad.copy.headline)}&adx_publisher_id=68&utm_source=facebook&adid={{ad.id}}&adx_terms=${encodedKeywords}&adx_lang=${campaignData.language || 'en'}&cmpgid={{campaign.id}}`
            }))
          });

          // If campaign was created successfully and we have an rsoc_spy ID, update it
          if (response.success && response.data?.campaign?.id && campaignData.selectedPage?.id) {
            try {
              await Api.updateRsocSpyCampaignId(campaignData.selectedPage.id, response.data.campaign.id);
              console.log('✅ Updated rsoc_spy campaign_id:', campaignData.selectedPage.id, response.data.campaign.id);
            } catch (updateError) {
              console.warn('⚠️ Failed to update rsoc_spy campaign_id:', updateError);
              // Don't fail the whole operation if this update fails
            }
          }

          return { account: account.name, success: response.success, error: response.error };
        } catch (error) {
          return { account: account.name, success: false, error: error.message };
        }
      });

      // Wait for all campaigns to be created
      const results = await Promise.all(campaignPromises);
      
      // Check results
      const successfulCampaigns = results.filter(r => r.success);
      const failedCampaigns = results.filter(r => !r.success);
      
      if (successfulCampaigns.length === 0) {
        throw new Error('All campaign creations failed');
      }
      
      let successMessage = `Successfully created ${successfulCampaigns.length} campaign${successfulCampaigns.length > 1 ? 's' : ''}`;
      if (successfulCampaigns.length > 0) {
        successMessage += ` for: ${successfulCampaigns.map(c => c.account).join(', ')}`;
      }
      
      if (failedCampaigns.length > 0) {
        successMessage += `\n\nFailed to create campaigns for: ${failedCampaigns.map(c => `${c.account} (${c.error})`).join(', ')}`;
      }
      
      setSuccess(successMessage);
      setActiveStep(steps.length);
    } catch (err) {
      setError('Failed to launch campaign: ' + err.message);
      console.error('Campaign launch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle step navigation
  const handleNext = () => {
    // Validate current step before proceeding
    if (!isStepValid(activeStep)) {
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setError(null);
    setSuccess(null);
    setCampaignData({
      selectedAccounts: [],
      selectedPage: null,
      campaign: {
        name: '',
        objective: 'OUTCOME_SALES',
        status: 'PAUSED',
        facebookPageId: '524097567461962', // Default Facebook page (Expected Unexpected)
        specialAdCategories: [], // Special ad categories
      },
      adSet: {
        name: '',
        optimization_goal: 'OFFSITE_CONVERSIONS',
        billing_event: 'IMPRESSIONS',
        budget: 20,
        targeting: {
          countries: [],
          interests: [],
        },
      },
      ads: [],
    });
  };

  // Validate current step
  const isStepValid = (step) => {
    setError(null);
    
    switch (step) {
      case 0: // Account selection
        if (!campaignData.selectedAccounts || campaignData.selectedAccounts.length === 0) {
          setError('Please select at least one Facebook account before proceeding');
          return false;
        }
        return true;
        
      case 1: // Page selection
        if (!campaignData.selectedPage) {
          setError('Please select a page before proceeding');
          return false;
        }
        return true;
        
      case 2: // Campaign config
        if (!campaignData.campaign.name || campaignData.campaign.name.trim() === '') {
          setError('Please enter a campaign name');
          return false;
        }
        if (!campaignData.campaign.objective) {
          setError('Please select a campaign objective');
          return false;
        }
        return true;
        
      case 3: // Ad Set config
        if (!campaignData.adSet.name || campaignData.adSet.name.trim() === '') {
          setError('Please enter an ad set name');
          return false;
        }
        if (!campaignData.adSet.budget || campaignData.adSet.budget <= 0) {
          setError('Please enter a valid budget amount greater than 0');
          return false;
        }
        if (!campaignData.adSet.targeting.countries || campaignData.adSet.targeting.countries.length === 0) {
          setError('Please select at least one country for targeting');
          return false;
        }
        return true;
        
      case 4: // Ad creation
        if (!campaignData.ads || campaignData.ads.length === 0) {
          setError('Please create at least one ad before proceeding');
          return false;
        }
        // Validate each ad has required components
        for (let i = 0; i < campaignData.ads.length; i++) {
          const ad = campaignData.ads[i];
          if (!ad.copy || !ad.copy.headline || !ad.copy.primaryText) {
            setError(`Ad ${i+1} is missing required copy (headline or primary text)`);
            return false;
          }
          if (!ad.image || !ad.image.url) {
            setError(`Ad ${i+1} is missing an image`);
            return false;
          }
        }
        return true;
        
      case 5: // Review & Launch
        // Validate all required data is present for launch
        if (!campaignData.selectedAccounts || campaignData.selectedAccounts.length === 0) {
          setError('Selected accounts are missing');
          return false;
        }
        if (!campaignData.selectedPage) {
          setError('Selected page is missing');
          return false;
        }
        if (!campaignData.campaign.name || campaignData.campaign.name.trim() === '') {
          setError('Campaign name is missing');
          return false;
        }
        if (!campaignData.adSet.name || campaignData.adSet.name.trim() === '') {
          setError('Ad set name is missing');
          return false;
        }
        if (!campaignData.adSet.budget || campaignData.adSet.budget <= 0) {
          setError('Valid budget is required');
          return false;
        }
        if (!campaignData.ads || campaignData.ads.length === 0) {
          setError('At least one ad is required');
          return false;
        }
        return true;
        
      default:
        return true;
    }
  };

  // Render current step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <AccountSelector
            accounts={facebookAccounts}
            selectedAccounts={campaignData.selectedAccounts}
            onAccountSelect={handleAccountSelect}
            multiSelect={true}
          />
        );
      case 1:
        return (
          <PageSelector
            onPageSelect={handlePageSelect}
            selectedPage={campaignData.selectedPage}
            useRsocSpy={true}
          />
        );
      case 2:
        return (
          <CampaignConfigurator
            campaignData={campaignData.campaign}
            onCampaignChange={handleCampaignChange}
            selectedPage={campaignData.selectedPage}
            facebookPages={facebookPages}
          />
        );
      case 3:
        return (
          <AdSetConfigurator
            adSetData={campaignData.adSet}
            onAdSetChange={handleAdSetChange}
          />
        );
      case 4:
        return (
          <AdCreator
            campaignData={campaignData}
            onAdsChange={handleAdsChange}
          />
        );
      case 5:
        return (
          <LaunchSummary
            campaignData={campaignData}
            onLaunch={handleLaunch}
            loading={loading}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <MainLayoutDemand>
      <Box sx={{ mb: 3 }}>
        <PageHeader 
          title="Campaign Launcher" 
          subtitle="Create and launch Facebook campaigns with AI-generated content"
        />
        <RsocNavHeader />
      </Box>

      <Container maxWidth="lg">
        {/* Launch Mode Toggle */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={launchMode}
            exclusive
            onChange={(event, newMode) => {
              if (newMode !== null) {
                setLaunchMode(newMode);
                // Reset state when switching modes
                setActiveStep(0);
                setError(null);
                setSuccess(null);
              }
            }}
            aria-label="launch mode"
          >
            <ToggleButton value="manual" aria-label="manual launch">
              Manual Launch
            </ToggleButton>
            <ToggleButton value="quick" aria-label="quick launch">
              Quick Launch
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
            variant="filled"
            elevation={6}
          >
            <Box sx={{ fontWeight: 'medium', mb: 0.5 }}>Error</Box>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Render Manual or Quick Launch based on mode */}
        {launchMode === 'manual' ? (
          <Paper 
            sx={{ 
              p: { xs: 2, md: 3 }, 
              mb: 3, 
              borderRadius: 2,
              boxShadow: 2
            }}
          >
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 2, mb: 2 }}>
            {activeStep === steps.length ? (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Campaign Launch Complete!
                </Typography>
                <Typography color="text.secondary" paragraph>
                  Your campaign has been created and {campaignData.campaign.status === 'ACTIVE' ? 'is now running' : 'is ready to be activated'}.
                </Typography>
                <Button onClick={handleReset} sx={{ mt: 2 }}>
                  Create Another Campaign
                </Button>
              </Box>
            ) : (
              <Box>
                {getStepContent(activeStep)}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={activeStep === steps.length - 1 ? handleLaunch : handleNext}
                    disabled={loading}
                  >
                    {activeStep === steps.length - 1 ? 'Launch Campaign' : 'Next'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
        ) : (
          <QuickLauncher 
            facebookAccounts={facebookAccounts}
            accountsLoading={accountsLoading}
            accountsError={accountsError}
            onError={setError}
            onSuccess={setSuccess}
          />
        )}
      </Container>
    </MainLayoutDemand>
  );
};

export default LauncherPage; 